/*
These links were helpful
https://docs.mapbox.com/help/tutorials/use-mapbox-gl-js-with-react/
https://github.com/mapbox/mapbox-react-examples
*/
import styled from 'styled-components';
import MDBox from "components/MDBox";

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import React, { useRef, useEffect, useState } from 'react';
import { downloadData } from 'aws-amplify/storage';
import mapboxgl from 'mapbox-gl';


mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapContainer = styled(MDBox)` 
  position: relative;

  .mapboxgl-map {
    border-radius: 12px;
  }

  .map-legend {
    position: absolute;
    bottom: 20px; 
    right: 20px; 
    background: rgba(255, 255, 255, 0.9); 
    border-radius: 8px;
    padding: 10px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    font-size: 12px;
    z-index: 999; /* Ensure it appears above the map */
    font-family: 'Roboto', sans-serif;

    h4 {
      margin: 0 0 10px 0;
      font-weight: bold; 
      font-size: 14px;
      text-align: center;
    }

    .gradient-bar {
      height: 15px; 
      width: 200px; 
      border-radius: 4px;
      background: linear-gradient(to right, #F9E6FA, #E20C8F);
    }

    .legend-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 5px;

      span {
        font-size: 10px;
        color: #555; 
      }
    }
  }

  .mapboxgl-popup.custom-popup {
    .mapboxgl-popup-content {
      max-width: 700px 
      min-width: 250px
      padding: 12px 
      font-size: 14px 
      border-radius: 8px 
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) 
      font-family: 'Roboto', sans-serif 
      line-height: 1.15 
      color: #333 
    }

    .mapboxgl-popup-close-button {
      background-color: gray 
      color: white 
      border-radius: 20% 
      width: 18px 
      height: 18px 
      line-height: 18px 
    }
  }
`;

//Used for managing popups in the mapbox geocoder, otherwise they do not get removed whenever a user searches for multiple cities in succession
let currentPopup = null;

function PointMapComponent({downloadUrl}) {

  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-98.5795, 39.8283],
      zoom: 3,
      attributionControl: false 
    });

    // zoom +/-
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', async () => {
      try{
          console.log("Fetching data from S3");
          const binaryPointData = await downloadData({path: downloadUrl}).result //Ethan: changed the path to the correct one
          console.log("--------------------------------------------------------");
          const pointText = await binaryPointData.body.text();
          const geojsonData = JSON.parse(pointText);
          const pointGeojsonData = JSON.parse(pointText);
          console.log("GeoJSON data parsed:", geojsonData);
          console.log("-------------------------------------");
          const pointData = pointGeojsonData;
          //to make it similar to folium's, else can do this in other ways too
          const values = pointData.features.map(f => f.properties[["Solar Sentiment Score"]]);
          const minValue = Math.min(...values);
          const maxValue = Math.max(...values);
          const colorScale = [
            [minValue, '#F9E6FA'],
            [maxValue, '#E20C8F']
          ];

          const localGeocoder = (query) => {
            return pointData.features.filter(f => 
              f.properties.Place.toLowerCase().includes(query.toLowerCase())
            ).map(feature => ({
              type: "Feature",
              geometry: feature.geometry,
              properties: feature.properties,
              place_name: `${feature.properties.Place}`,
              center: feature.geometry.coordinates, 
            }))
          }

          const geocoder = new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl,
            localGeocoder: localGeocoder,
            localGeocoderOnly: true,
            marker: false,
            placeholder: 'Search for locations', 
            zoom: 8
          });

          geocoder.on('result', e => {
            if(currentPopup) {
              currentPopup.remove();
            }
            const place = e.result;
            currentPopup = new mapboxgl.Popup({ className: 'custom-popup' }) // add custom class for styling
              .setLngLat(place.center)
              .setHTML(`<span style="font-size: 1.3em"><strong>${place.properties.Place}</strong> (${place.properties.Year})</span>
                <br><br><span style="font-size: 1.5em; color: #E20C8F;">${place.properties['Percentile'].toFixed(0)}th percentile</span> <span style="font-size: 1.3em;">nationwide for solar sentiment</span>
                <br><br>Solar sentiment score of <strong>${place.properties['Solar Sentiment Score'].toFixed(2)}</strong> based on ${place.properties['No. of Tweets'].toLocaleString()} posts from ${place.properties['No. of Users'].toLocaleString()} unique X (Twitter) users`
              )
              .addTo(map.current);
          })

          map.current.addControl(geocoder, 'top-left');

          map.current.addSource('point-data', {
            type: 'geojson',
            data: pointData
          });
                    
          map.current.addLayer({
            id: 'point-data-layer',
            type: 'circle',
            source: 'point-data',
            paint: {
              'circle-color': [
                'interpolate',
                ['linear'],
                ['get', 'Solar Sentiment Score'],
                ...colorScale.flat()
              ],
              'circle-opacity': 0.7,
              'circle-radius': 5 
            }
          })

          map.current.on('click', (e) => {
            const pointFeatures = map.current.queryRenderedFeatures(e.point, {
              layers: ['point-data-layer']
            });
          
            if (pointFeatures.length > 0) {
              new mapboxgl.Popup({ className: 'custom-popup' })
                .setLngLat(e.lngLat)
                .setHTML(`<span style="font-size: 1.3em"><strong>${pointFeatures[0].properties.Place}</strong> (${pointFeatures[0].properties.Year})</span>
                  <br><br><span style="font-size: 1.7em; color: #E20C8F;">${pointFeatures[0].properties['Percentile'].toFixed(0)}th percentile</span> <span style="font-size: 1.3em;">nationwide for solar sentiment</span>
                  <br><br>Solar sentiment score of <strong>${pointFeatures[0].properties['Solar Sentiment Score'].toFixed(2)}</strong> based on ${pointFeatures[0].properties['No. of Tweets'].toLocaleString()} posts from ${pointFeatures[0].properties['No. of Users'].toLocaleString()} unique X (Twitter) users`
                )
                .addTo(map.current);
            }
          });

          // Change cursor to pointer when hovering over a FIPS area
          map.current.on('mouseenter', 'point-data-layer', () => {
            map.current.getCanvas().style.cursor = 'pointer';
          });

          // Change cursor back when leaving a FIPS area
          map.current.on('mouseleave', 'point-data-layer', () => {
            map.current.getCanvas().style.cursor = '';
          });

          console.log("Map layers added successfully");
          
        }
        catch(error){
          console.error('Error loading GeoJSON:', error);
          setMapError('Error loading map data: ' + error.message);
        }
    });
  }, []);

  if (mapError) {
    // return <div>Error: {mapError}</div>;
    return <MDBox>Error: {mapError}</MDBox>;
  }

  return (
    <MapContainer ref={mapContainer} height="600px">
      <div className="map-legend">
        <h4>Solar Sentiment Score</h4>
        <div 
          className="gradient-bar" 
          style={{ background: 'linear-gradient(to right, #F9E6FA, #E20C8F)' }} 
        ></div>
        <div className="legend-labels">
          <span>Negative (-10)</span>
          <span>Positive (+10)</span>
        </div>
      </div>
    </MapContainer>
  );
}

export default PointMapComponent;